import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { Close } from '@/components/Icons/Close';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { ButtonVariant, Size } from '@/types/components';

interface Props {
  title: string;
  saveLabel?: string;
  isSaveDisabled?: boolean;
  isSaving?: boolean;
  children: ReactNode;
  onBack?: () => void;
  onClose: () => void;
  onSave?: () => void;
}

export const DetailLayout: React.FC<Props> = ({
  title,
  saveLabel,
  isSaveDisabled,
  isSaving,
  children,
  onBack,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const { progress, handleScroll } = useScrollFadeProgress(16);

  return (
    <div className="flex h-full flex-col max-md:animate-zoom-in">
      <div className="relative flex shrink-0 items-center gap-3 bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:p-5">
        <button
          type="button"
          onClick={onBack ?? onClose}
          aria-label={t('global.back')}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
        >
          <ArrowLeft />
        </button>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label={t('global.back')}
            className="hidden size-6 shrink-0 cursor-pointer items-center justify-center md:flex"
          >
            <ChevronRight
              strokeWidth="2"
              className="size-3.5 rotate-180 text-muted-foreground"
            />
          </button>
        )}
        <h2
          style={{ opacity: progress }}
          className="absolute left-1/2 -translate-x-1/2 text-callout-16 text-foreground md:hidden"
        >
          {title}
        </h2>
        <h2 className="hidden flex-1 text-headline-20 text-foreground md:block">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('global.close')}
          className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
        >
          <Close />
        </button>
      </div>

      <div
        onScroll={handleScroll}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-background px-3 pt-2 pb-4 [scrollbar-gutter:stable_both-edges]"
      >
        <h2
          style={{ opacity: 1 - progress }}
          className="pb-2 text-title-36 transition-opacity duration-[50ms] md:hidden"
        >
          {title}
        </h2>
        {children}
      </div>

      {onSave && (
        <div className="flex shrink-0 flex-col border-t border-divider bg-background px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:border-t-0 md:p-5">
          <Button
            variant={ButtonVariant.Primary}
            size={Size.Large}
            disabled={isSaveDisabled}
            isLoading={isSaving}
            onClick={onSave}
            className="w-full"
          >
            {saveLabel ?? t('global.save')}
          </Button>
        </div>
      )}
    </div>
  );
};
